import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavigationType, useNavigationType } from 'react-router-dom';
import { useUpdateSearchParams } from '../../hooks/useUpdateSearchParams';
import {
    isValidTableOrder,
    parsePageSize,
    readPageSize,
    writePageSize,
    readTableOrder,
    removeTableOrder,
    writeTableOrder,
} from './tablePreferencesStorage';

export const DEFAULT_PAGE_SIZE = 10;

function getPage(searchParams: URLSearchParams): number {
    const page = Number(searchParams.get('page'));
    return !Number.isNaN(page) && page > 0 ? page : 1;
}

export interface UseOrderAndPaginationControlReturn {
    readonly sortableFields: readonly string[];
    orderBy: string | null;
    setOrderBy: Dispatch<SetStateAction<string | null>>;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    currentPage: number;
    setCurrentPage: Dispatch<SetStateAction<number>>;
    totalCount: number;
    setTotalCount: (value: number) => void;
    pageCount: number;
    showPagination: boolean;
}

export function useResetPageOnFilterChange(
    committedFilterSignature: string,
    currentPage: number,
    setCurrentPage: UseOrderAndPaginationControlReturn['setCurrentPage']
): number {
    const previousCommittedFilterSignatureRef = useRef(committedFilterSignature);
    const filtersChanged = previousCommittedFilterSignatureRef.current !== committedFilterSignature;
    const effectiveCurrentPage = filtersChanged ? 1 : currentPage;

    useEffect(() => {
        if (filtersChanged && currentPage !== 1) {
            setCurrentPage(1);
        }

        previousCommittedFilterSignatureRef.current = committedFilterSignature;
    }, [committedFilterSignature, currentPage, filtersChanged, setCurrentPage]);

    return effectiveCurrentPage;
}

interface TablePreferenceOptions<T extends string | number> {
    tableId: string;
    parameter: string;
    parse: (value: string | null) => T | null | undefined;
    read: (tableId: string) => T | null | undefined;
    write: (tableId: string, value: T) => void;
    remove?: (tableId: string) => void;
    clearInvalid?: boolean;
}

function useTablePreference<T extends string | number>({
    tableId,
    parameter,
    parse,
    read,
    write,
    remove,
    clearInvalid = false,
}: TablePreferenceOptions<T>): T | null {
    const { searchParams, updateSearchParams } = useUpdateSearchParams();
    const navigationType = useNavigationType();
    const rawValue = searchParams.get(parameter);
    const urlValue = parse(rawValue) ?? null;
    const previousRef = useRef<{ tableId: string; valid: boolean } | null>(null);
    const previous = previousRef.current;
    // History entries without a preference must not inherit a later saved value.
    const restore =
        rawValue === null &&
        (previous === null ||
            (navigationType !== NavigationType.Pop &&
                (previous.tableId !== tableId || previous.valid)));
    const storedValue = restore ? read(tableId) ?? null : null;
    const clearedInHistory =
        rawValue === null &&
        navigationType === NavigationType.Pop &&
        previous?.tableId === tableId &&
        previous.valid;

    useEffect(() => {
        previousRef.current = { tableId, valid: urlValue !== null };
        if (rawValue === null) {
            if (storedValue !== null) {
                updateSearchParams(parameter, storedValue, { replace: true });
            } else if (clearedInHistory) {
                remove?.(tableId);
            }
        } else if (urlValue !== null) {
            write(tableId, urlValue);
        } else if (clearInvalid) {
            updateSearchParams(parameter, '', { replace: true });
        }
    }, [
        tableId,
        parameter,
        rawValue,
        urlValue,
        storedValue,
        write,
        remove,
        clearedInHistory,
        clearInvalid,
        updateSearchParams,
    ]);

    return urlValue ?? storedValue;
}

interface UseOrderAndPaginationControlOptions {
    readonly tableId: string;
    readonly sortableFields: readonly string[];
    readonly pageSize?: number;
}

export function useOrderAndPaginationControl({
    tableId,
    sortableFields,
    pageSize: defaultPageSize = DEFAULT_PAGE_SIZE,
}: UseOrderAndPaginationControlOptions): UseOrderAndPaginationControlReturn {
    const { searchParams, updateSearchParams, updateSearchParamsBatch } = useUpdateSearchParams();
    const urlPageSize = searchParams.get('pageSize');
    const pageSize =
        useTablePreference({
            tableId,
            parameter: 'pageSize',
            parse: parsePageSize,
            read: readPageSize,
            write: writePageSize,
            clearInvalid: true,
        }) ?? defaultPageSize;
    const writeOrder = useCallback(
        (id: string, value: string) => writeTableOrder(id, value, sortableFields),
        [sortableFields]
    );
    const orderBy = useTablePreference({
        tableId,
        parameter: 'orderBy',
        parse: (value) => (isValidTableOrder(value, sortableFields) ? value : null),
        read: (id) => readTableOrder(id, sortableFields),
        write: writeOrder,
        remove: removeTableOrder,
        clearInvalid: true,
    });
    const orderByRef = useRef(orderBy);
    orderByRef.current = orderBy;

    const setOrderBy = useCallback<Dispatch<SetStateAction<string | null>>>(
        (nextOrderByAction) => {
            const nextOrderBy =
                typeof nextOrderByAction === 'function'
                    ? nextOrderByAction(orderByRef.current)
                    : nextOrderByAction;

            if (nextOrderBy === null) {
                removeTableOrder(tableId);
            } else {
                writeOrder(tableId, nextOrderBy);
            }

            orderByRef.current = nextOrderBy;
            updateSearchParams('orderBy', nextOrderBy ?? '');
        },
        [tableId, updateSearchParams, writeOrder]
    );
    const urlPage = getPage(searchParams);
    const [pageState, setCurrentPage] = useState<number>(urlPage);
    const [totalCount, setTotalCountState] = useState<number>(0);
    const [previousUrlPage, setPreviousUrlPage] = useState(urlPage);
    const urlPageChanged = urlPage !== previousUrlPage;
    // Synchronize during render so a synchronous query-store update cannot see the
    // new URL with the old page state and write that old page back to the URL.
    if (urlPageChanged) {
        setPreviousUrlPage(urlPage);
        setCurrentPage(urlPage);
    }
    const currentPage = pageState;
    const currentPageRef = useRef<number>(currentPage);

    const setPageSize = useCallback(
        (nextPageSize: number) => {
            if (parsePageSize(nextPageSize) === undefined || nextPageSize === pageSize) return;
            // Preserve the previous size for Back, even if the URL used the default.
            if (urlPageSize === null) {
                updateSearchParams('pageSize', pageSize, { replace: true });
            }
            setCurrentPage(1);
            updateSearchParamsBatch({ pageSize: nextPageSize, page: 1 });
        },
        [pageSize, urlPageSize, updateSearchParams, updateSearchParamsBatch]
    );

    const setTotalCount = useCallback(
        (nextTotalCount: number) => {
            setTotalCountState(nextTotalCount);

            const lastValidPage = Math.max(Math.ceil(nextTotalCount / pageSize), 1);

            if (currentPageRef.current > lastValidPage) {
                setCurrentPage(lastValidPage);
            }
        },
        [pageSize]
    );

    // Keep ref in sync with currentPage.
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    // Update the page param in the URL.
    useEffect(() => {
        updateSearchParams('page', currentPage);
    }, [currentPage, updateSearchParams]);

    const pageCount = Math.ceil(totalCount / pageSize);

    return {
        orderBy,
        sortableFields,
        setOrderBy,
        pageSize,
        setPageSize,
        currentPage,
        setCurrentPage,
        totalCount,
        setTotalCount,
        pageCount,
        showPagination: pageCount > 0,
    };
}
